import { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { DynamicFormApi, ApiCategory } from "../DynamicForm";
import { SelectLocationDropdown } from "../SelectLocationDropdown";
import apiClient from "../../services/api";

export const OrganizationPage = () => {
  const { questionId: questionIdParam } = useParams<{ questionId?: string }>();
  const questionIdFromUrl =
    questionIdParam != null && questionIdParam !== ""
      ? (() => {
          const n = parseInt(questionIdParam, 10);
          return Number.isNaN(n) ? null : n;
        })()
      : null;

  const [categories, setCategories] = useState<ApiCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [currentStep, setCurrentStep] = useState(0);
  const [selectedAimag, setSelectedAimag] = useState("");
  const [selectedSum, setSelectedSum] = useState("");
  const [selectedBag, setSelectedBag] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [answers, setAnswers] = useState<Record<number, string | string[]>>({});

  const formLink = typeof window !== "undefined" ? window.location.pathname : "/forum/organization";

  const [formQuestionId, setFormQuestionId] = useState<number | null>(null);
  const [questionTitle, setQuestionTitle] = useState<string>("");

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const query =
        questionIdFromUrl != null
          ? `questionId=${encodeURIComponent(questionIdFromUrl)}`
          : `link=${encodeURIComponent(formLink)}`;
      const res = await apiClient.get<{
        success: boolean;
        data: ApiCategory[];
        questionId?: number;
        questionTitle?: string;
      }>(`/organization/categories/public?${query}`);
      if (res.data?.success && Array.isArray(res.data.data)) {
        setCategories(res.data.data);
        if (questionIdFromUrl != null) setFormQuestionId(questionIdFromUrl);
        else if (res.data.questionId != null) setFormQuestionId(res.data.questionId);
        if (res.data.questionTitle != null && res.data.questionTitle !== "")
          setQuestionTitle(res.data.questionTitle);
        else setQuestionTitle("");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [formLink, questionIdFromUrl]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const totalSteps = Math.max(1, categories.length + 1); // 0 = location, 1..n = categories
  const progressPercent = totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 0;

  const canProceedFromLocation = selectedAimag.trim() !== "" && selectedSum.trim() !== "" && selectedBag.trim() !== "" && businessType.trim() !== "";

  const currentCategory = currentStep > 0 && currentStep <= categories.length ? categories[currentStep - 1] : null;
  const currentCategoryFilled =
    currentCategory?.questions?.every((q) => {
      const v = answers[q.id];
      if (q.answerType === "multi_select") return Array.isArray(v) ? v.length > 0 : false;
      return v != null && String(v).trim() !== "";
    }) ?? false;

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleNext = () => {
    if (currentStep === 0 && !canProceedFromLocation) return;
    if (currentStep > 0 && currentCategory && !currentCategoryFilled) return;
    if (currentStep < totalSteps - 1) {
      setCurrentStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSubmit = async () => {
    if (currentStep === 0 && !canProceedFromLocation) return;
    if (currentStep > 0 && categories[currentStep - 1]) {
      const lastCategory = categories[currentStep - 1];
      const lastFilled = lastCategory?.questions?.every((q) => {
        const v = answers[q.id];
        if (q.answerType === "multi_select") return Array.isArray(v) ? v.length > 0 : false;
        return v != null && String(v).trim() !== "";
      });
      if (!lastFilled) return;
    }

    const questionId = formQuestionId ?? null;

    const payload: {
      aimag: string;
      sum: string;
      bag: string;
      businessType: string;
      questionId?: number;
      answers: Record<number, string | string[]>;
    } = {
      aimag: selectedAimag.trim(),
      sum: selectedSum.trim(),
      bag: selectedBag.trim(),
      businessType: businessType.trim(),
      answers,
    };
    if (questionId != null) payload.questionId = questionId;

    try {
      setSubmitting(true);
      const res = await apiClient.post("/organization/submissions", payload);
      if (res.data?.success) {
        setSubmitSuccess(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        alert((res.data as { message?: string })?.message || "Илгээхэд алдаа гарлаа");
      }
    } catch (err: unknown) {
      const msg = err && typeof err === "object" && "message" in err ? String((err as { message: string }).message) : "Илгээхэд алдаа гарлаа";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const isLastStep = currentStep === totalSteps - 1;
  const noCategories = categories.length === 0;
  const showSubmitButton =
    (isLastStep && noCategories && canProceedFromLocation) ||
    (isLastStep && currentCategory && currentCategoryFilled);

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-gray-50 py-6 sm:py-10 px-4 flex items-center justify-center">
        <p className="text-gray-600">Ачаалж байна...</p>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div className="min-h-screen w-full bg-gray-50 py-6 sm:py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-white rounded-2xl border border-gray-100 p-10">
            <h2 className="text-xl font-bold text-green-600 mb-2">Амжилттай илгээгдлээ</h2>
            <p className="text-gray-600 mb-6">Таны хариултыг хүлээн авлаа. Баярлалаа.</p>
            <Link to="/forum" className="inline-block px-6 py-2.5 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600">
              Forum руу буцах
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-50 py-6 sm:py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <Link
            to="/forum"
            className="inline-flex items-center text-gray-600 hover:text-amber-600 transition-colors font-medium bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-100"
          >
            ← Буцах
          </Link>
        </div>

        <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
          <div className="px-4 py-6 sm:px-10 bg-amber-500 text-white text-center">
            <h1 className="text-sm sm:text-xl font-bold leading-relaxed">
              {questionTitle || "Хуулийн этгээд, аж ахуйн нэгж, байгууллага гамшгийн бэлэн байдлаа шалгах хяналтын хуудас"}
            </h1>
          </div>

          <div className="px-6 py-8 sm:px-10">
            <div className="w-full h-3 bg-gray-100 rounded-full mb-8 overflow-hidden">
              <div
                className="bg-amber-500 h-full rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            {/* Step 0: Location + Business */}
            {currentStep === 0 && (
              <div className="space-y-8 animate-fadeIn">
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 space-y-6">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-600 text-sm font-bold">1</span>
                      Байгууллагын хаягийн мэдээлэл (Аймаг/Хот, Сум/Дүүрэг, Баг/Хороо) – заавал бөглөнө
                    </h3>
                    <SelectLocationDropdown
                      setSelectedAimag={setSelectedAimag}
                      selectedAimag={selectedAimag}
                      setSelectedSum={setSelectedSum}
                      selectedSum={selectedSum}
                      setSelectedBag={setSelectedBag}
                      selectedBag={selectedBag}
                    />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-600 text-sm font-bold">2</span>
                      Үйл ажиллагааны чиглэл – заавал бөглөнө
                    </h3>
                    <input
                      value={businessType}
                      onChange={(e) => setBusinessType(e.target.value)}
                      placeholder="Жишээ нь: Худалдаа, үйлдвэрлэл..."
                      className="w-full h-12 bg-white border border-gray-300 rounded-lg px-4 text-base focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-shadow"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 1..n: Categories */}
            {currentStep > 0 && currentCategory && (
              <div className="animate-fadeIn">
                <DynamicFormApi category={currentCategory} answers={answers} setAnswers={setAnswers} />
              </div>
            )}

            <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between items-center">
              {currentStep > 0 ? (
                <button
                  onClick={handlePrevious}
                  className="px-6 py-2.5 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-xl font-semibold transition-colors"
                >
                  Өмнөх
                </button>
              ) : (
                <div />
              )}

              {showSubmitButton ? (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-6 py-2.5 bg-green-600 text-white shadow-lg shadow-green-200 hover:bg-green-700 rounded-xl font-semibold transition-all transform hover:-translate-y-0.5 disabled:opacity-50"
                >
                  {submitting ? "Илгээж байна..." : "Хариултыг илгээх"}
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={(currentStep === 0 && !canProceedFromLocation) || (currentStep > 0 && !currentCategoryFilled)}
                  className="px-6 py-2.5 bg-amber-500 text-white shadow-lg shadow-amber-200 hover:bg-amber-600 rounded-xl font-semibold transition-all transform hover:-translate-y-0.5 disabled:opacity-50"
                >
                  Дараах
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
